import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  signUp as apiSignUp,
  signIn as apiSignIn,
  signInWithGoogle as apiSignInWithGoogle,
  resetPassword as apiResetPassword,
  signOut as apiSignOut,
  SignUpData,
  LoginData,
} from "@services/supabaseAPI";
import { getCurrentUserInfo } from "@services/api";
import { supabase } from "@services/supabaseAPI";
import { UserState } from "../../types/types";

interface AuthState {
  userInfo: UserState | null | undefined;
  isError: boolean;
  isSuccess: boolean;
  isLoading: boolean;
  message: string;
  isForgotPassword: boolean;
  authChecked: boolean;
  // Transient: a SIGNED_IN gate is in flight — the AAL/MFA probe + the
  // bootstrap that sets userId. authChecked can be true (the initial
  // getSession finished with no session) while this is still resolving, so
  // RequireAuth must wait on it rather than treat the empty userId as
  // "logged out" and redirect.
  authResolving: boolean;
  // Transient: a SIGNED_IN event landed with AAL1 + a verified factor
  // requiring step-up. While true, the rest of the auth bootstrap is
  // suppressed (userId stays empty, RequireAuth keeps the user out
  // of the app) and AuthenticationModal renders the MFA challenge
  // form inline. Cleared once the user reaches AAL2 (or resets via
  // a recovery code).
  mfaPending: boolean;
}

const initialState: AuthState = {
  userInfo: null,
  isError: false,
  isSuccess: false,
  isLoading: false,
  message: "",
  isForgotPassword: false,
  authChecked: false,
  authResolving: false,
  mfaPending: false,
};

//Need to change any here
export const signup = createAsyncThunk(
  "auth/signup",
  async (signUpData: SignUpData, thunkAPI) => {
    // Sign up with Supabase Auth
    const result = await apiSignUp(signUpData);
    if (!result.success) {
      console.error("Signup error:", result.error);
      return thunkAPI.rejectWithValue(result.error);
    }

    // NOTE: We don't create the user in the app database here.
    // The idempotent /users/me endpoint will automatically create the user
    // when AuthContext calls addUserInfo() after signup completes.
    // This approach:
    // - Simplifies error handling (no partial signup state)
    // - Uses the same code path for signup and login recovery
    // - Ensures user creation always succeeds (with retry logic in the service)

    return result.data;
  }
);

export const login = createAsyncThunk(
  "auth/login",
  async (loginData: LoginData, thunkAPI) => {
    const result = await apiSignIn(loginData);
    if (!result.success) {
      return thunkAPI.rejectWithValue(result.error);
    }
    return result.data;
  }
);

export const loginWithGoogle = createAsyncThunk(
  "auth/loginWithGoogle",
  async (_: void, thunkAPI) => {
    const result = await apiSignInWithGoogle();
    if (!result.success) {
      return thunkAPI.rejectWithValue(result.error);
    }
    return result.data;
  }
);

export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async (email: string, thunkAPI) => {
    const result = await apiResetPassword(email);
    if (!result.success) {
      return thunkAPI.rejectWithValue(result.error);
    }
    return result.data;
  }
);
export const logout = createAsyncThunk("auth/logout", async () => {
  return await apiSignOut();
});

export const addUserInfo = createAsyncThunk(
  "auth/addUser",
  async (_userId?: string) => {
    // userId parameter is kept for backwards compatibility but not used
    // The Go API gets the user from the JWT token
    const result = await getCurrentUserInfo();
    if (!result.success) {
      throw new Error(result.error.message);
    }
    // Email lives in Supabase Auth (auth.users), not the app's users
    // table — merge it in here so consumers can read it from Redux.
    const { data: sessionData } = await supabase.auth.getSession();
    return {
      ...result.data,
      email: sessionData.session?.user?.email ?? null,
    };
  }
);

export const authSlice = createSlice({
  name: "auth",
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    reset: (state) => {
      state.isLoading = false;
      state.isError = false;
      state.isSuccess = false;
      state.message = "";
      state.isForgotPassword = false;
    },
    clearAll: () => {},

    setError: (state, action) => {
      state.isError = action.payload;
    },

    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },

    setAuthChecked: (state, action) => {
      state.authChecked = action.payload;
    },

    setAuthResolving: (state, action) => {
      state.authResolving = action.payload;
    },

    setMfaPending: (state, action) => {
      state.mfaPending = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(signup.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(signup.fulfilled, (state) => {
        state.isLoading = false;
        state.isSuccess = true;
      })
      .addCase(signup.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string; // Fix this
      })
      .addCase(logout.fulfilled, (state) => {
        state.userInfo = null;
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = false;
        state.message = "";
      })
      .addCase(login.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(login.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string; // Fix this
      })
      .addCase(loginWithGoogle.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(loginWithGoogle.fulfilled, (state) => {
        // The tab is about to navigate to the provider; clearing the
        // flag matters only if the user comes straight back (e.g.
        // browser back from the consent screen) — a stuck spinner
        // otherwise.
        state.isLoading = false;
      })
      .addCase(loginWithGoogle.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload as string;
      })
      .addCase(addUserInfo.fulfilled, (state, action) => {
        state.userInfo = action.payload;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.isForgotPassword = true;
      });
  },
});

export const { reset, clearAll, setError, setLoading, setAuthChecked, setAuthResolving, setMfaPending } = authSlice.actions;
export default authSlice.reducer;
